import { useState } from 'react'
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { Alert, App, Button, Modal, Skeleton } from 'antd'
import { useTRPC } from '@/trpc/react.ts'
import { MoneyEntry } from './money-entry.tsx'
import { toCents } from './utils/money.ts'
import { refreshMoneyQueries } from './utils/refresh-money-queries.ts'

export function GroupMoneyEntry({ groupId, onClose }: { groupId: number; onClose: () => void }) {
  const trpc = useTRPC()
  const cache = useQueryClient()
  const { message } = App.useApp()
  const [open, setOpen] = useState(true)
  const [saved, setSaved] = useState(false)
  // 每次打开重新核对组成员与余额，填写期间不随后台刷新重置收款表单。
  const group = useQuery({
    ...trpc.bill.groupDetail.queryOptions({ groupId }),
    staleTime: Infinity,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  })
  const currentGroup = group.data
  let unavailableReason: string | undefined
  if (currentGroup && currentGroup.status !== 'issued') {
    unavailableReason = '仅已出单的收款账单组可以登记收款，请刷新后核对组单状态'
  } else if (currentGroup && toCents(currentGroup.outstandingAmount) <= BigInt(0)) {
    unavailableReason = '此收款账单组已无待收金额，请刷新后核对收款记录'
  }
  const billIds = currentGroup?.items.filter((item) => !item.releasedAt).map((item) => item.billId) ?? []
  const canLoadBills = group.isSuccess && group.isFetchedAfterMount && !unavailableReason
  const queries = useQueries({
    queries: (canLoadBills ? billIds : []).map((billId) => ({
      ...trpc.bill.detail.queryOptions({ billId }),
      staleTime: Infinity,
      refetchOnMount: 'always' as const,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false
    }))
  })
  const close = () => setOpen(false)
  const afterClose = async () => {
    if (saved) {
      try {
        await refreshMoneyQueries(cache, trpc)
      } catch {
        void message.error('收款已登记，刷新失败，请刷新页面')
      }
    }
    onClose()
  }
  if (
    canLoadBills &&
    billIds.length &&
    queries.every((query) => query.isFetchedAfterMount) &&
    queries.every((query) => query.isSuccess)
  ) {
    return (
      <MoneyEntry
        bills={queries.map((query) => query.data)}
        initialAccountId={currentGroup?.collectionAccountId ?? undefined}
        open={open}
        afterClose={() => void afterClose()}
        onClose={close}
        onSaved={async () => {
          setSaved(true)
          close()
        }}
      />
    )
  }
  const error = group.error ?? queries.find((query) => query.isError)?.error
  let content = <Skeleton active />
  if (error) {
    content = (
      <Alert
        type="error"
        title={error.message}
        action={
          <Button
            onClick={() => {
              if (group.isError) {
                void group.refetch()
                return
              }
              void Promise.all(queries.map((query) => query.refetch()))
            }}
          >
            重试
          </Button>
        }
      />
    )
  } else if (!group.isFetching && unavailableReason) {
    content = <Alert type="warning" title={unavailableReason} />
  } else if (canLoadBills && !billIds.length) {
    content = <Alert type="warning" title="此收款账单组没有可结算账单，请刷新后核对组单状态" />
  }
  return (
    <Modal open={open} title="登记收款" onCancel={onClose} footer={null}>
      {content}
    </Modal>
  )
}
